import type { ResolvedEdgeStyle } from "../types/style.js";
import type { HopMark, LayoutEdge } from "./layout-types.js";
import { el, escapeXml } from "./svg-builder.js";

interface Point {
  x: number;
  y: number;
}

const round2 = (n: number): number => Number(n.toFixed(2));

/**
 * The segment index of `points` whose line the hop's centre lies on, or -1.
 * A hop is derived from a crossing *on* one of the edge's segments, so the
 * centre projects onto that segment with ~zero perpendicular distance.
 */
function hopSegment(points: Point[], hop: HopMark): number {
  for (let i = 0; i < points.length - 1; i++) {
    const p = points[i];
    const q = points[i + 1];
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) continue;
    const t = ((hop.x - p.x) * dx + (hop.y - p.y) * dy) / len2;
    if (t < -1e-6 || t > 1 + 1e-6) continue;
    const distX = hop.x - (p.x + dx * t);
    const distY = hop.y - (p.y + dy * t);
    if (distX * distX + distY * distY < 0.01) return i;
  }
  return -1;
}

/**
 * Build the edge's stroke path with a **gap** at each hop, so a hop reads as a
 * real jump-over (the arc drawn in the crossing-marks layer bridges the gap)
 * instead of an arc laid on top of a continuous line (a "half-moon"). The gap
 * `[centre ± halfWidth]` along the hosting segment exactly matches the arc's
 * endpoints (same formula), so line and arc meet seamlessly. The crossed line is
 * not a host and stays continuous — it is the through-line the hop jumps over.
 */
function gappedStrokePath(points: Point[], hops: HopMark[]): string {
  const cutsPerSeg = new Map<number, { tEnter: number; tExit: number }[]>();
  for (const hop of hops) {
    const seg = hopSegment(points, hop);
    if (seg < 0) continue;
    const p = points[seg];
    const q = points[seg + 1];
    const len2 = (q.x - p.x) ** 2 + (q.y - p.y) ** 2;
    if (len2 < 1e-9) continue;
    const rad = (hop.angle * Math.PI) / 180;
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);
    const paramOf = (x: number, y: number) =>
      ((x - p.x) * (q.x - p.x) + (y - p.y) * (q.y - p.y)) / len2;
    let tEnter = paramOf(hop.x - hop.halfWidth * dx, hop.y - hop.halfWidth * dy);
    let tExit = paramOf(hop.x + hop.halfWidth * dx, hop.y + hop.halfWidth * dy);
    if (tEnter > tExit) [tEnter, tExit] = [tExit, tEnter];
    tEnter = Math.max(0, tEnter);
    tExit = Math.min(1, tExit);
    if (tExit - tEnter <= 0) continue;
    const list = cutsPerSeg.get(seg);
    if (list) list.push({ tEnter, tExit });
    else cutsPerSeg.set(seg, [{ tEnter, tExit }]);
  }

  let d = `M ${round2(points[0].x)} ${round2(points[0].y)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p = points[i];
    const q = points[i + 1];
    const at = (t: number) => `${round2(p.x + (q.x - p.x) * t)} ${round2(p.y + (q.y - p.y) * t)}`;
    for (const { tEnter, tExit } of (cutsPerSeg.get(i) ?? []).sort((a, b) => a.tEnter - b.tEnter)) {
      d += ` L ${at(tEnter)} M ${at(tExit)}`;
    }
    d += ` L ${round2(q.x)} ${round2(q.y)}`;
  }
  return d;
}

const STROKE_DASHARRAY: Record<ResolvedEdgeStyle["strokeStyle"], string | undefined> = {
  solid: undefined,
  dashed: "8 4",
  dotted: "2 2",
};

/**
 * Visible thickness of one facet casing per side, matching the node ring's
 * `RING_WIDTH` so an edge's membership reads at the same weight as a card's.
 *
 * The rings leave a 1px gap between bands; the casings cannot, and deliberately
 * do not try. A ring is its own rect over the canvas, so the background shows
 * through between two of them. Casings are stacked strokes on one line, and the
 * only way to separate them would be to paint a canvas-coloured band in the
 * middle — which is a lie over any non-default background. Abutting bands of
 * equal width is the honest rendering of the same idea.
 */
const CASING_WIDTH = 3;

/**
 * One casing per selected facet, widest first, so each is covered by the next
 * narrower one and band `i` is left showing as the annulus
 * `[strokeWidth/2 + i*W, strokeWidth/2 + (i+1)*W]` around the line. Every band
 * is the same width, and the innermost one — against the line — is the first
 * facet in known-facet order, which is the radial position ring `i` takes
 * around a card.
 *
 * Solid regardless of the edge's `stroke-style`, and with no `marker-end`: a
 * dashed casing would read as a second dashed edge, and a marker would stamp an
 * oversized arrowhead behind the real one. The node rings make the same choice
 * — they ignore `border-style` — because the highlight belongs to the reader's
 * selection, not to the element's styling.
 *
 * Painted through `style=` rather than presentation attributes. Diff mode
 * injects a stylesheet that repaints every `line` / `path` under a
 * `[data-diff-state]` group (`diff/diff-style.ts`), and a selector rule beats a
 * presentation attribute — so on a removed edge the casings would come out red
 * and dashed, losing the facet identity they exist to carry. An inline style
 * outranks that rule, which is what keeps "PII is teal" true in diff mode too.
 */
function renderFacetCasings(
  facets: readonly string[],
  colorOf: ReadonlyMap<string, string>,
  strokeWidth: number,
  strokedShape: (attrs: Record<string, string | number | undefined>) => string,
): string[] {
  const casings: string[] = [];
  for (let i = facets.length - 1; i >= 0; i--) {
    const color = colorOf.get(facets[i]);
    if (!color) continue;
    const width = strokeWidth + 2 * (i + 1) * CASING_WIDTH;
    casings.push(
      strokedShape({
        style: `stroke:${color};stroke-width:${width};stroke-dasharray:none`,
        "stroke-linecap": "butt",
        "data-facet-casing": facets[i],
      }),
    );
  }
  return casings;
}

export function renderEdge(
  edge: LayoutEdge,
  style: ResolvedEdgeStyle,
  markerId: string,
  diffState?: string,
  hops?: HopMark[],
  labelAnchorOverride?: Point,
  /**
   * Selected facets this edge belongs to, in known-facet order (#2544). Empty
   * whenever the overlay is off, which is what keeps a file that writes
   * `facets` on an edge byte-identical while nothing is selected (TPL-2174).
   */
  facets: readonly string[] = [],
  /** Colour per facet id; absent when the overlay is off. */
  facetColorOf?: ReadonlyMap<string, string>,
): string {
  const { fromPoint, toPoint } = edge;
  const points: Point[] = [fromPoint, ...(edge.waypoints ?? []), toPoint];
  const parts: string[] = [];
  // When this edge hosts hop marks (#1859 P2c-C), draw its stroke with a gap at
  // each hop so the arc reads as a jump-over, not a bump on a solid line.
  const gapped = hops !== undefined && hops.length > 0;

  // Group-by against-flow edges are dashed (#1859) so backward inter-group
  // dependencies stand out — but only when the author left `stroke-style` at
  // its default, so an explicit style still wins (mirrors the label-position
  // override precedence, ADR-1185).
  const dashArray =
    edge.groupBackward && style.strokeStyle === "solid"
      ? STROKE_DASHARRAY.dashed
      : STROKE_DASHARRAY[style.strokeStyle];

  const strokeAttrs = {
    stroke: style.color,
    "stroke-width": style.strokeWidth,
    "stroke-dasharray": dashArray,
    "marker-end": `url(#${markerId})`,
    class: edge.cyclic ? "krs-edge--cyclic" : undefined,
  };

  // Two separate questions, deliberately not folded into one flag.
  //
  // `interactive` drives the `krs-edge--interactive` class, and that class is
  // the *right-click* advertisement: `preview.css` gives it `cursor:
  // context-menu`, and `handleContextMenu` opens the direction menu only for
  // an edge addressable by an `edge#<id>` selector. Widening it to
  // detail-carrying edges would promise a menu that cannot open (#2543).
  //
  // `needsHitArea` drives the wide invisible hit-line behind the visible
  // stroke, which keeps the click target practical on default 1.5px edges
  // without changing the diagram's visual weight. A property block gives an
  // edge something to open a panel with on *left* click, so it needs the hit
  // area even when a base collision cleared its id.
  const hasDetail =
    edge.description !== undefined || (edge.links !== undefined && edge.links.length > 0);
  const interactive = edge.canonicalId !== undefined;
  const needsHitArea = interactive || hasDetail;
  if (needsHitArea) {
    if (points.length === 2) {
      parts.push(
        el("line", {
          x1: fromPoint.x,
          y1: fromPoint.y,
          x2: toPoint.x,
          y2: toPoint.y,
          stroke: "transparent",
          "stroke-width": 14,
          "stroke-linecap": "butt",
          class: "krs-edge__hitline",
        }),
      );
    } else {
      parts.push(
        el("polyline", {
          points: points.map((p) => `${p.x},${p.y}`).join(" "),
          fill: "none",
          stroke: "transparent",
          "stroke-width": 14,
          "stroke-linecap": "butt",
          class: "krs-edge__hitline",
        }),
      );
    }
  }

  /**
   * The edge's own geometry, stroked with `attrs`. One reader for the visible
   * stroke and the facet casings below it, so a casing can never trace a
   * different path than the line it sits under. The geometry itself is resolved
   * once here rather than per call — `gappedStrokePath` walks every hop, and an
   * edge in N facets asks for the same shape N+1 times.
   */
  const geometry: Record<string, string | number | undefined> = gapped
    ? { d: gappedStrokePath(points, hops), fill: "none" }
    : points.length === 2
      ? { x1: fromPoint.x, y1: fromPoint.y, x2: toPoint.x, y2: toPoint.y }
      : { points: points.map((p) => `${p.x},${p.y}`).join(" "), fill: "none" };
  const shapeTag = gapped ? "path" : points.length === 2 ? "line" : "polyline";
  const strokedShape = (attrs: Record<string, string | number | undefined>): string =>
    el(shapeTag, { ...geometry, ...attrs });

  // Facet casings (#2544): the edge counterpart of the node's concentric rings.
  // Drawn widest first and then covered by the narrower ones, so each facet is
  // left showing as its own band and the innermost band — the one against the
  // line — is the same facet on every edge, exactly as `renderFacetRings` makes
  // the innermost ring the same facet on every card. Always solid and never
  // carrying the arrow marker: this is the highlight, not a second edge.
  if (facets.length > 0 && facetColorOf) {
    parts.push(...renderFacetCasings(facets, facetColorOf, style.strokeWidth, strokedShape));
  }

  parts.push(strokedShape(strokeAttrs));

  if (edge.label) {
    // Default anchor (parallel-bundle slide applied, ADR-1185). When the
    // auto label-placement pass (#2048) has nudged this label off a collision,
    // it hands the resolved anchor in via `labelAnchorOverride`, which wins.
    const { x: midX, y: midY } =
      labelAnchorOverride ??
      labelAnchor(
        points,
        resolveLabelPosition(edge, style),
        style.labelOffsetX,
        style.labelOffsetY,
      );
    const labelText = el(
      "text",
      {
        x: midX,
        y: midY - 6,
        "text-anchor": "middle",
        fill: style.color,
        "font-size": `${style.fontSize}px`,
        "font-family": "sans-serif",
      },
      escapeXml(edge.label),
    );

    if (edge.domainEdges && edge.domainEdges.length > 0) {
      // Wrap in a clickable group so PreviewPane can open a detail panel on click.
      // A transparent hit rect widens the click target beyond the text bounding box.
      const hitRect = el("rect", {
        x: midX - 60,
        y: midY - 20,
        width: 120,
        height: 18,
        fill: "transparent",
        "pointer-events": "all",
      });
      parts.push(
        el(
          "g",
          {
            "data-domain-edges": JSON.stringify(edge.domainEdges),
            style: "cursor:pointer",
          },
          hitRect + "\n" + labelText,
        ),
      );
    } else {
      parts.push(labelText);
    }
  }

  return el(
    "g",
    {
      "data-edge-from": edge.from,
      "data-edge-to": edge.to,
      "data-edge-kind": edge.kind,
      "data-edge-canonical-id": edge.canonicalId,
      "data-edge-label": edge.syntheticLabel ? undefined : edge.label || undefined,
      // The property block's payload, read back by the app to open
      // EdgeDetailPanel on a left click (#2543). Emitted only when authored, so
      // a file that writes no block produces byte-identical SVG.
      "data-edge-description": edge.description,
      "data-edge-links":
        edge.links !== undefined && edge.links.length > 0
          ? JSON.stringify(edge.links.map((l) => ({ url: l.url, label: l.label })))
          : undefined,
      // Selected facets this edge belongs to, spelled the way a node card
      // spells them (#2544). Absent while the overlay is off.
      "data-facet-member": facets.length > 0 ? facets.join(" ") : undefined,
      "data-diff-state": diffState,
      class: interactive ? "krs-edge krs-edge--interactive" : "krs-edge",
    },
    parts.join("\n"),
  );
}

/**
 * The fractional label position for an edge: the author's `label-position`
 * (default `0.5`), or — when left at default on a parallel-edge bundle (N ≥ 2
 * sharing `(from, to)`) — a per-index slide so parallel labels separate
 * (ADR-1185). Extracted so the auto label-placement pass (#2048) computes the
 * same default anchor `renderEdge` would draw.
 */
export function resolveLabelPosition(edge: LayoutEdge, style: ResolvedEdgeStyle): number {
  let position = style.labelPosition;
  if (
    position === 0.5 &&
    edge.bundleSize !== undefined &&
    edge.bundleIndex !== undefined &&
    edge.bundleSize >= 2
  ) {
    position = (edge.bundleIndex + 1) / (edge.bundleSize + 1);
  }
  return position;
}

/**
 * Anchor point for the edge label.
 *
 * When `position === 0.5` and both offsets are `0`, the historical
 * "longest segment midpoint" heuristic fires — keeping unstyled
 * diagrams visually stable. Once the author sets `label-position` or
 * `label-offset` on the edge, the renderer switches to fractional
 * traversal (anchor at `position * totalLength` along the polyline) and
 * adds the screen-axis offsets `(offsetX, offsetY)` on top.
 *
 * Offsets are screen-axis (not edge-perpendicular) so a global rule
 * like `edge { label-offset: 0 8px; }` produces a uniform downward
 * shift across the diagram regardless of each edge's slope.
 */
function labelAnchor(points: Point[], position: number, offsetX: number, offsetY: number): Point {
  return labelAnchorWithSegment(points, position, offsetX, offsetY).anchor;
}

/**
 * As {@link labelAnchor}, but also returns `segDir` — the direction vector of
 * the polyline segment the anchor lies on (the longest one for the default
 * heuristic, or the one the fractional position falls in). The auto placement
 * pass (#2048) nudges *perpendicular* to this local segment, not the overall
 * `from → to` chord, so the lift stays square to the line the label is drawn on
 * for bent / waypoint routes.
 */
export function labelAnchorWithSegment(
  points: Point[],
  position: number,
  offsetX: number,
  offsetY: number,
): { anchor: Point; segDir: Point } {
  if (position === 0.5 && offsetX === 0 && offsetY === 0) {
    return defaultLabelAnchor(points);
  }
  return fractionalLabelAnchor(points, position, offsetX, offsetY);
}

function segDirAt(points: Point[], i: number): Point {
  return { x: points[i + 1].x - points[i].x, y: points[i + 1].y - points[i].y };
}

function defaultLabelAnchor(points: Point[]): { anchor: Point; segDir: Point } {
  if (points.length === 2) {
    return {
      anchor: {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2,
      },
      segDir: segDirAt(points, 0),
    };
  }
  let bestIdx = 0;
  let bestLen = -1;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    const dy = points[i + 1].y - points[i].y;
    const len = dx * dx + dy * dy;
    if (len > bestLen) {
      bestLen = len;
      bestIdx = i;
    }
  }
  return {
    anchor: {
      x: (points[bestIdx].x + points[bestIdx + 1].x) / 2,
      y: (points[bestIdx].y + points[bestIdx + 1].y) / 2,
    },
    segDir: segDirAt(points, bestIdx),
  };
}

function fractionalLabelAnchor(
  points: Point[],
  position: number,
  offsetX: number,
  offsetY: number,
): { anchor: Point; segDir: Point } {
  const segLengths: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    const dy = points[i + 1].y - points[i].y;
    const len = Math.hypot(dx, dy);
    segLengths.push(len);
    total += len;
  }
  if (total === 0) {
    return {
      anchor: { x: points[0].x + offsetX, y: points[0].y + offsetY },
      segDir: segDirAt(points, 0),
    };
  }

  const clamped = Math.min(1, Math.max(0, position));
  const target = clamped * total;
  let acc = 0;
  for (let i = 0; i < segLengths.length; i++) {
    const segLen = segLengths[i];
    const reachable = acc + segLen;
    const isLast = i === segLengths.length - 1;
    if (reachable >= target || isLast) {
      const localT = segLen === 0 ? 0 : (target - acc) / segLen;
      const dx = points[i + 1].x - points[i].x;
      const dy = points[i + 1].y - points[i].y;
      return {
        anchor: {
          x: points[i].x + dx * localT + offsetX,
          y: points[i].y + dy * localT + offsetY,
        },
        segDir: segDirAt(points, i),
      };
    }
    acc += segLen;
  }
  // Unreachable in practice — the loop above covers `isLast`.
  return {
    anchor: { x: points[points.length - 1].x + offsetX, y: points[points.length - 1].y + offsetY },
    segDir: segDirAt(points, points.length - 2),
  };
}

export function renderArrowMarker(id: string, color: string): string {
  return el(
    "marker",
    {
      id,
      viewBox: "0 0 10 10",
      refX: 10,
      refY: 5,
      markerWidth: 8,
      markerHeight: 8,
      orient: "auto-start-reverse",
    },
    el("path", { d: "M 0 0 L 10 5 L 0 10 z", fill: color }),
  );
}
